import React, { useEffect, useState, useCallback, useMemo } from "react";
import { ChefHat, Loader2, ChevronDown, Snowflake, Refrigerator, Copy } from "lucide-react";
import { SectionHeading } from "@/components/wizard/ui";
import api from "@/lib/api";
import { toast } from "sonner";
import { DEMAND_COLOR } from "@/lib/planModel";

// Local error boundary — the Batch Cook Sheet is a nice-to-have; a crash
// here MUST NOT blank the whole Wizard. Any render/hook error inside
// BatchPrepCard is swallowed and the card silently hides itself.
class BatchErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) { console.error("BatchPrepCard crashed:", err); }
  render() { return this.state.hasError ? null : this.props.children; }
}

// Batch-cook prep card — surfaces the hidden work the engine already does
// (repeating one lunch/dinner recipe Mon–Thu with per-day scaled portions)
// as an explicit shopping-list-style prep sheet. Only shown when the
// athlete opted into "batch" cooking style.
// Round a scaled quantity down to a cook-friendly value (5g/ml chunks;
// whole pieces). Mirrors backend `_round_qty` so per-day splits agree with
// the shopping list.
function roundCookQty(qty, unit) {
  if (qty == null) return 0;
  if (unit === "g" || unit === "ml") {
    if (qty >= 20) return Math.floor(qty / 5) * 5;
    return Math.max(1, Math.floor(qty));
  }
  if (unit === "pcs") return Math.max(0.5, Math.floor(qty * 2) / 2);
  return Math.round(qty * 10) / 10;
}

function BatchRow({ batch }) {
  if (!batch) return null;
  const days = Array.isArray(batch.days) ? batch.days : [];
  const ingredients = Array.isArray(batch.ingredients) ? batch.ingredients : [];
  const isFridge = (batch.dayCount || days.length) < 4;
  // Per-day split of every ingredient — scaled by that day's portion %.
  const portionFor = (d) => ingredients.map((ing) => ({
    name: ing.name,
    unit: ing.unit,
    qty: roundCookQty((ing.qty || 0) * ((d.portionPct || 0) / 100), ing.unit),
  }));
  return (
    <div className="border border-white/10 bg-[#141414]" data-testid={`batch-row-${batch.id}`}>
      {/* CTA-style header block — icon square + title + macro strip */}
      <div className="flex items-stretch">
        <div className="shrink-0 grid place-items-center bg-[#E2E30A] w-16 sm:w-20">
          <ChefHat className="h-6 w-6 sm:h-7 sm:w-7 text-black" strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0 px-4 py-3 border-b border-white/10">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#E2E30A] font-bold mb-1">Cook once · use {batch.dayCount}x</p>
          <p className="text-base sm:text-lg font-bold text-white leading-tight break-words">{batch.name}</p>
          <p className="text-[10px] text-zinc-400 mt-1 font-mono uppercase tracking-wider">
            {batch.totalKcal} kcal · {batch.totalProtein}P · {batch.totalCarb}C total
          </p>
        </div>
      </div>

      {/* Cook once — full ingredient list, chip layout */}
      <div className="px-4 sm:px-5 py-4 border-b border-white/5">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#E2E30A] font-bold mb-3">Total to cook</p>
        <div className="flex flex-wrap gap-2">
          {ingredients.map((ing) => (
            <span key={ing.name}
              className="inline-flex items-baseline gap-1.5 border border-white/15 bg-black/30 px-2.5 py-1.5">
              <span className="text-[11px] text-zinc-300">{ing.name}</span>
              <span className="text-[12px] font-mono font-bold text-white">{ing.qty}{ing.unit}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Per-day split — CTA tile grid so every day is a proper card */}
      <div className="px-4 sm:px-5 py-4">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#E2E30A] font-bold mb-3">Portion each day</p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {days.map((d) => {
            const parts = portionFor(d);
            const dot = DEMAND_COLOR[d.demand] || "#71717A";
            return (
              <div key={d.index} className="border border-white/10 bg-black/40 p-3" data-testid={`batch-day-${batch.id}-${d.index}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 shrink-0" style={{ background: dot }} />
                    <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white">{(d.day || "").slice(0, 3)}</span>
                  </div>
                  <span className="font-mono text-[10px] text-zinc-300">{d.portionPct || 0}% · {Math.round(d.kcal || 0)}kcal</span>
                </div>
                <div className="space-y-0.5">
                  {parts.map((p) => (
                    <div key={p.name} className="flex justify-between text-[11px] leading-tight">
                      <span className="text-zinc-400 truncate mr-2">{p.name}</span>
                      <span className="font-mono text-white font-semibold shrink-0">{p.qty}{p.unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Storage hint */}
      <div className="flex items-start gap-2 border-t border-white/5 bg-[#E2E30A]/5 px-4 sm:px-5 py-2.5 text-[11px] text-zinc-200">
        {isFridge ? <Refrigerator className="h-3.5 w-3.5 mt-0.5 text-[#E2E30A] shrink-0" />
                 : <Snowflake className="h-3.5 w-3.5 mt-0.5 text-[#00E5FF] shrink-0" />}
        <span>{batch.storageHint}</span>
      </div>
    </div>
  );
}

export default function BatchPrepCard(props) {
  return <BatchErrorBoundary><BatchPrepCardInner {...props} /></BatchErrorBoundary>;
}

function BatchPrepCardInner({ plan }) {
  const { generated, selections, profile } = plan || {};
  const isBatch = profile?.cookingStyle === "batch";
  const [batches, setBatches] = useState(generated?.batchPrep || []);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isBatch || !generated?.dayPathways) return;
    setLoading(true);
    try {
      const sel = {};
      Object.keys(selections || {}).forEach((k) => { sel[String(k)] = selections[k]; });
      const { data } = await api.post("/engine/batch-prep", {
        dayPathways: generated.dayPathways, selections: sel,
      });
      setBatches(Array.isArray(data?.batchPrep) ? data.batchPrep : []);
    } catch (e) { console.error("Batch prep refresh failed:", e?.message || e); }
    finally { setLoading(false); }
  }, [isBatch, generated, selections]);

  const pathSig = useMemo(() => {
    try {
      return JSON.stringify(
        (generated?.dayPathways || []).map((d) => Object.values(d?.pathways || {})
          .map((p) => (p?.meals || []).map((m) => `${m?.id || ""}:${m?.scale || 1}`))));
    } catch { return ""; }
  }, [generated]);
  const selSig = useMemo(() => { try { return JSON.stringify(selections || {}); } catch { return ""; } }, [selections]);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [selSig, pathSig, isBatch]);

  const copySheet = async () => {
    if (!batches.length) return;
    const txt = batches.map((b) => {
      const ings = Array.isArray(b.ingredients) ? b.ingredients : [];
      const days = Array.isArray(b.days) ? b.days : [];
      return [
        `${b.name.toUpperCase()} — ${b.dayCount} portions · ${b.totalKcal} kcal total`,
        "Cook once:",
        ...ings.map((ing) => `  - ${ing.name}: ${ing.qty}${ing.unit}`),
        "Portion split:",
        ...days.map((d) => {
          const parts = ings.map((ing) => {
            const q = roundCookQty((ing.qty || 0) * ((d.portionPct || 0) / 100), ing.unit);
            return `${ing.name} ${q}${ing.unit}`;
          }).join(" · ");
          return `  - ${d.day}: ${d.portionPct}% (${Math.round(d.kcal)} kcal) — ${parts}`;
        }),
        b.storageHint,
      ].join("\n");
    }).join("\n\n");
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Batch prep sheet copied");
    } catch { toast.error("Copy failed — please try again"); }
  };

  if (!isBatch) return null;
  if (!generated?.dayPathways) return null;
  if (!plan) return null;
  const safeBatches = Array.isArray(batches) ? batches : [];
  if (loading && safeBatches.length === 0) {
    return (
      <div className="border border-white/10 bg-[#141414] p-6 mb-6 text-sm text-zinc-300 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Building your batch-prep sheet…
      </div>
    );
  }
  if (!safeBatches.length) return null;

  return (
    <div className="mb-6" data-testid="batch-prep-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-2 no-print">
        <SectionHeading eyebrow="Prep Once" title="Batch Cook Sheet"
          desc="Same recipe across the week, portions scaled per day to hit each day's fuel target. Cook the total, split as shown." />
        <button type="button" data-testid="batch-copy-btn" onClick={copySheet}
          className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/40 px-3 py-1.5 text-[11px] uppercase tracking-wider text-white transition-colors">
          <Copy className="h-3.5 w-3.5" /> Copy sheet
        </button>
      </div>
      <div className="space-y-3">
        {safeBatches.map((b) => <BatchRow key={b.id} batch={b} />)}
      </div>
    </div>
  );
}
